// ** Modules
import React from 'react';
import { useQuery } from '@apollo/client/react';
import { gql, type TypedDocumentNode } from '@apollo/client';

// ** Utils
import {
	CtaFields,
	TextBlock,
	FileBlock,
	HeroBlock,
	TeamBlock,
	VideoBlock,
	FileFields,
	ImageBlock,
	ButtonBlock,
	MediaFields,
	PersonBlock,
	CardsRowBlock,
	PartnerBlock,
	BildTextBlock,
	AccordionGroup,
	RecursivePageLink,
	ImageElementContent,
	RelevantPersonFields
} from './fragments';

// ** Types
import type {
	Query,
	PaginationArg,
	PublicationStatus,
	Enum_News_Newscategory,
	QueryAllNews_ConnectionArgs,
	NewsEntityResponseCollection
} from '@/types/graphql';

// ** Types
export type NewsPeriod = {
	publishedAt: string;
};

export type NewsPeriodConnection = {
	nodes: NewsPeriod[];
	pageInfo: {
		total: number;
	};
};

// Update this in your data return type
type NewsData = {
	loading: boolean;
	error: any;
	refetch: () => Promise<any>;
	allNews_connection: NewsEntityResponseCollection;
};

type NewsCategoryProps = {
	locale: string;
	allLabel?: string | null;
};

type NewsBySlugVariables = {
	slug: string;
	locale?: string;
	pagination?: Partial<PaginationArg> | Record<string, any>;
	status?: PublicationStatus | string | any;
};

// ** Queries Schemas
export const getNews: TypedDocumentNode<Pick<Query, 'allNews_connection'>> = gql`
    ${MediaFields}

    query News(
        $locale: I18NLocaleCode
        $filters: NewsFiltersInput
        $pagination: PaginationArg
        $sort: [String]
        $status: PublicationStatus
    ) {
        allNews_connection(
            locale: $locale
            filters: $filters
            sort: $sort
            pagination: $pagination
            status: $status
        ) {
            nodes {
                slug
                newsTitle
                newsCategory
				allowHierarchy
                newsDescription
				setPublishedDate
                newsManualPublishedDate
                tags {
                    name
                    slug
                }
                newsBild {
                    ...MediaFields
                }
                publishedAt
            }
            pageInfo {
                page
                pageSize
                pageCount
                total
                __typename
            }
        }
    }
`;

export const getNewsCategories: TypedDocumentNode<Pick<Query, 'allNews_connection'>> = gql`
	query GetAllNewsCategories($locale: I18NLocaleCode) {
		allNews_connection(
			locale: $locale, 
			pagination: { page: 1, pageSize: 1000 }
		) {
			nodes {
				newsCategory
			}
		}
	}
`;

export const getNewsPeriods: TypedDocumentNode<Pick<Query, 'allNewsPeriods'>> = gql`
	query GetNewsPeriods($locale: I18NLocaleCode) {
		allNewsPeriods(locale: $locale) {
			nodes {
				publishedAt
			}
			pageInfo {
				total
			}
		}
	}
`;

export const getNewsBySlug: TypedDocumentNode<Pick<Query, 'allNews'>, NewsBySlugVariables> = gql`
	${CtaFields}
	${FileBlock}
	${HeroBlock}
	${TeamBlock}
	${TextBlock}
	${FileFields}
	${VideoBlock}
	${ImageBlock}
	${MediaFields}
	${PersonBlock}
	${ButtonBlock}
	${PartnerBlock}
	${BildTextBlock}
	${CardsRowBlock}
	${AccordionGroup}
	${RecursivePageLink}
	${ImageElementContent}
	${RelevantPersonFields}

	query NewsBySlug($slug: String!, $locale: I18NLocaleCode, $pagination: PaginationArg) {
		allNews(filters: { slug: { eq: $slug } }, locale: $locale, pagination: $pagination, status: PUBLISHED) {
			__typename
			slug
			newsTitle
			newsDescription
			publishedAt
			tags {
				name
				slug
			}
			newsBild {
				...MediaFields
			}
			newsHero {
				...HeroBlock
			}
			newsContent {
				__typename
				...TextBlock
				...BildTextBlock
				...PersonBlock
				...CardsRowBlock
				...VideoBlock
				...FileBlock
				...PartnerBlock
				...TeamBlock
				...AccordionGroup
				...ButtonBlock
				...ImageBlock
			}
		}
	}
`;

//////////////////
// ** Query Functions

// Get News
export const useGetNews = ({ sort, locale, filters, pagination }: QueryAllNews_ConnectionArgs): NewsData => {
	const shouldSkip = !locale || locale === null;
	const { loading, error, data, refetch } = useQuery(getNews, {
		ssr: false,
		variables: {
			locale,
			pagination: {
				page: pagination?.page || 1,
				pageSize: pagination?.pageSize || 12
			},
			sort: sort,
			filters,
			status: 'PUBLISHED'
		},
		skip: shouldSkip,
		fetchPolicy: 'cache-and-network'
	});

	React.useEffect(() => {
		if (error) {
			console.error('[NEWS DEBUG] allNews_connection failed', {
				locale,
				filters,
				sort,
				pagination,
				error: error instanceof Error ? error.message : String(error),
				dataKeys: data ? Object.keys(data) : []
			});
			return;
		}

		if (!loading && data?.allNews_connection) {
			console.info('[NEWS DEBUG] allNews_connection result', {
				locale,
				count: data.allNews_connection.nodes?.length ?? 0,
				total: data.allNews_connection.pageInfo?.total ?? 0
			});
		}
	}, [data, error, filters, locale, loading, pagination, sort]);

	if (!loading && !error && !data?.allNews_connection) {
		throw new Error(`Required data missing for news`);
	}

	if (loading || shouldSkip || error) {
		return {
			loading,
			error,
			refetch,
			allNews_connection:
				(data?.allNews_connection as NewsEntityResponseCollection) ?? ({} as NewsEntityResponseCollection)
		};
	}

	if (!data?.allNews_connection) {
		throw new Error(`Required data missing for news`);
	}

	return {
		loading,
		error,
		refetch,
		allNews_connection: data.allNews_connection
	};
};

// Get News Categories
export const useGetNewsCategories = ({ locale, allLabel = 'Alle Kategorien' }: NewsCategoryProps) => {
	const { data, loading } = useQuery(getNewsCategories, {
		variables: { locale },
		skip: !locale,
		fetchPolicy: 'cache-first'
	});

	const categories = React.useMemo(() => {
		const nodes = (data?.allNews_connection?.nodes || []) as Array<
			NonNullable<NonNullable<NewsData['allNews_connection']>['nodes']>[number]
		>;

		const uniqueValues = Array.from(
			new Set(nodes.map((n) => n.newsCategory).filter((val): val is Enum_News_Newscategory => Boolean(val)))
		).sort();

		const options = uniqueValues.map((val) => ({
			value: val,
			label: val
		}));

		return [{ value: 'all', label: allLabel }, ...options];
	}, [data, allLabel]);

	return { categories, loading };
};

// Get News Periods
export const useGetNewsPeriods = ({ locale }: { locale: string }) => {
	const { data, loading, error } = useQuery(getNewsPeriods, {
		variables: { locale },
		skip: !locale
	});

	const nodes = data?.allNewsPeriods?.nodes || [];

	const periods = React.useMemo(() => {
		const options = nodes.reduce((acc: any[], curr: any) => {
			if (!curr.publishedAt) return acc;

			const raw = curr.publishedAt; // e.g., "01/01/2025 00:00"
			let yearValue = '';

			if (raw.includes('/')) {
				// 1. Remove time: "01/01/2025 00:00" -> "01/01/2025"
				// 2. Get last part: "01/01/2025" -> "2025"
				yearValue = raw.split(' ')[0].split('/').pop() || '';
			} else {
				// Fallback for ISO: "2025-01-01" -> "2025"
				yearValue = raw.split('-')[0];
			}

			// Clean up and de-duplicate
			const cleanYear = yearValue.trim();
			if (cleanYear && /^\d{4}$/.test(cleanYear) && !acc.find((o) => o.value === cleanYear)) {
				acc.push({
					value: cleanYear,
					label: cleanYear
				});
			}
			return acc;
		}, []);

		// Sort descending: 2025, 2024...
		const sorted = options.sort((a, b) => b.value.localeCompare(a.value));

		return [{ value: 'all', label: 'Alle Zeiträume' }, ...sorted];
	}, [nodes]); // 'nodes' is now correctly defined in the outer scope

	return { periods, loading, error };
};
